import { DropDownConstantsFactory } from 'src/app/base/shared/syncfusion/dropdown-constants-factory';
import { FormBuilder, Validators } from '@angular/forms';
import { ClientDTO, FilterCondition, FilterDTO, FilterOperator, ZoneDamageDTO, ZoneDamageState } from '../../base/models/models.swagger.generated';
import { DataManager } from '@syncfusion/ej2-data';
import { AvpCustomAdaptor } from '../../base/shared/syncfusion/avp-custom-adaptor';
import { SpinnerService } from '../../base/spinner/spinner.service';
import { ZoneDamageService } from './zone-damage.service';
import { GridBaseComponent } from 'src/app/base/shared/grid-base/grid-base.component';
import { Component, HostListener, OnInit } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ClientService } from 'src/app/base/services/client/client.service';
import { map, startWith } from 'rxjs/operators';
import { ZoneDamageEditFormComponent } from './zone-damage-edit-form/zone-damage-edit-form.component';
import { GridModule } from '@syncfusion/ej2-angular-grids';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { AsyncPipe, DatePipe } from '@angular/common';
import { ClientAutocompleteComponent } from '../../base/shared/client-autocomplete/client-autocomplete.component';
import { TitleBarComponent } from '../../base/shared/title-bar/title-bar.component';
import { ZoneDamageEditFormType } from './zone-damage-edit-form/zone-damage-edit-form.type';

@Component({
    selector: 'app-zone-damage',
    templateUrl: './zone-damage.component.html',
    styleUrls: ['./zone-damage.component.scss'],
    imports: [
        TitleBarComponent,
        ClientAutocompleteComponent,
        DropDownListModule,
        GridModule,
        ZoneDamageEditFormComponent,
        AsyncPipe,
        DatePipe,
        TranslateModule,
    ]
})
export class ZoneDamageComponent extends GridBaseComponent implements OnInit {

  public ZoneDamageState = ZoneDamageState;

  public dropdownSettings = DropDownConstantsFactory.createStandardClientDropdownSettings();
  public notificationList$ = this.translateService.onLangChange
    .pipe(
      startWith(this.translateService.currentLang),
      map(_ => [
        {name: this.translateService.instant('i18n.domain.zone-damage.notifications.closed'), id: 'done'},
        {name: this.translateService.instant('i18n.domain.zone-damage.notifications.open'), id: 'pending'},
      ]),
    );

  constructor(translateService: TranslateService,
              clientService: ClientService,
              public zoneDamageService: ZoneDamageService,
              private fb: FormBuilder,
              private spinnerService: SpinnerService) {
    super(clientService, translateService, zoneDamageService);
  }

  @HostListener('window:beforeunload')
  beforeUnload() {
    return !this.gridEditForm?.dirty;
  }

  public override get selectedClient(): ClientDTO {
    return this.clientService.selectedClient;
  }

  public override set selectedClient(value: ClientDTO) {
    if (this.clientService.selectedClient != value) {
      this.clientService.selectedClient = value;
      this.onClientSelected(value);
    }
  }

  public ngOnInit(): void {
    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(this.zoneDamageService, this.spinnerService),
    });
    this.isActiveColumnName = 'state';
    this.editDialogTitle = 'i18n.domain.zone-damage.edit-damage';
    this.addDialogTitle = 'i18n.domain.zone-damage.add-damage';
    this.addIndexFindColumnName = 'id';
  }

  public applyFilters(event: any): void {
    const filters = [];
    if (event === 'done') {
      filters.push(new FilterDTO({
        isComplex: false,
        field: 'State',
        operator: FilterOperator.Equal,
        value: ZoneDamageState[ZoneDamageState.Repaired],
      }));
    }
    if (event === 'pending') {
      filters.push(new FilterDTO({
        isComplex: true,
        condition: FilterCondition.Or,
        predicates: [
          new FilterDTO({
            isComplex: false, field: 'State',
            operator: FilterOperator.Equal,
            value: ZoneDamageState[ZoneDamageState.New],
          }),
          new FilterDTO({
            isComplex: false,
            field: 'State',
            operator: FilterOperator.Equal,
            value: ZoneDamageState[ZoneDamageState.RepairInProgress],
          }),
          new FilterDTO({
            isComplex: false,
            field: 'State',
            operator: FilterOperator.Equal,
            value: ZoneDamageState[ZoneDamageState.Acknowledged],
          }),
        ],
      }));
    }
    this.zoneDamageService.filters = filters;
    this.grid.refresh();
  }

  protected initializeEditForm(data: ZoneDamageDTO): void {
    this.gridEditForm = this.fb.group<ZoneDamageEditFormType>({
      id: this.fb.control(data.id),
      state: this.fb.control<string>(ZoneDamageState[data.state], Validators.required),
      clientId: this.fb.control(data.clientId || this.clientService.selectedClient.id),
      zone: this.fb.control(data.zone?.id, Validators.required),
      deviceId: this.fb.control(data.deviceId, Validators.required),
      damageDescription: this.fb.control(data.damageDescription),
      reportTime: this.fb.control({value: data.reportTime.toFormat('dd.MM.yyyy HH:mm:ss'), disabled: true}),
      reportPositionLatitude: this.fb.control(data.reportPositionLatitude),
      reportPositionLongitude: this.fb.control(data.reportPositionLongitude),
      position: this.fb.control(data.reportPositionLatitude ? data.reportPositionLatitude + ',' + data.reportPositionLongitude : '', Validators.required),
      reporter: this.fb.control({value: data.reporter?.firstname ? data.reporter?.firstname + ' ' + data.reporter?.lastname : '', disabled: true}),
      phoneNumber: this.fb.control({value: data.reporter?.phoneNumber, disabled: true}),
    });
  }

  protected getEditDialogTitleParams(rowData: any) {
    return {params: ''};
  }

  protected override getDataToSave(): any {
    const formValue = this.gridEditForm.value;
    const positions = this.gridEditForm.value.position.split(',');

    formValue.reportPositionLatitude = positions[0];
    formValue.reportPositionLongitude = positions[1];
    formValue.zoneId = formValue.zone;
    delete formValue.reportTime;
    return formValue;
  }

}
